import React, { useEffect } from 'react'
import { useState } from "react"
import { Box, Container, Typography } from "@mui/material"
import { SettingsSidebar } from "../../../../components/dashboardcomponents/KnowledgeBase/Customization/SettingsSidebar"
import { PreviewSection } from "../../../../components/dashboardcomponents/KnowledgeBase/Customization/PreviewSection"
import { useDispatch, useSelector } from "react-redux"
import { fetchKbSettings, saveKbSettings } from "../../../../store/features/kbSettings"
import { toast } from "react-toastify"
import GroupSelectDropdown from "../../../../components/dashboardcomponents/ProfileComponents/GroupSelectDropdown"
import { useAnalyticsGroupId } from "../../../../hooks/useAnalyticsGroupId"
import { isHeaderInlinePageTitleEnabled } from "../../../../config/featureFlags"
import "../../TicketManagment/style.css"

const DEFAULT_BRAND_COLOR = "#148DFD";
const DEFAULT_ACCENT_COLOR = "#556575";

const emptySettings = () => ({
  brand_color: DEFAULT_BRAND_COLOR,
  accent_color: DEFAULT_ACCENT_COLOR,
  font_family: "Inter",
  custom_domain: "",
  base_path: "/",
  isCustomDomainEnabled: false,
  featuredArticles: [],
  logo: null,
  logoPreview: null,
  business_name: "",
});

export default function CustomizationPage() {
  const dispatch = useDispatch()
  const business_id = useSelector((state) => state.loginToken?.response?.user?.business_id)
  const { settings: fetchedSettings, loading: settingsLoading, hasFetched, lastFetchedGroupId } = useSelector((state) => state.kbSettings)
  const [initialSyncDone, setInitialSyncDone] = useState(false)
  const { selectedGroupId, setSelectedGroupId } = useAnalyticsGroupId()
  const isInitialLoading = !selectedGroupId || settingsLoading || !hasFetched || !initialSyncDone;

  const [settings, setSettings] = useState(emptySettings)

  const [currentTab, setCurrentTab] = useState(0)

  // Fetch settings when group is selected
  useEffect(() => {
    if (business_id && selectedGroupId) {
      setInitialSyncDone(false)
      setSettings((prev) => ({
        ...prev,
        logo: null,
        logoPreview: null,
      }))
      dispatch(fetchKbSettings({ business_id, group_id: selectedGroupId }))
    }
  }, [business_id, selectedGroupId, dispatch])

  // Prefill local state when fetched settings arrive for the active group
  useEffect(() => {
    if (
      !hasFetched ||
      !selectedGroupId ||
      String(lastFetchedGroupId) !== String(selectedGroupId)
    ) {
      return
    }

    if (fetchedSettings) {
      setSettings({
        brand_color: fetchedSettings.brand_color || DEFAULT_BRAND_COLOR,
        accent_color: fetchedSettings.accent_color || DEFAULT_ACCENT_COLOR,
        font_family: fetchedSettings.font_family || "Inter",
        custom_domain: fetchedSettings.custom_domain || "",
        base_path: fetchedSettings.base_path || "/",
        isCustomDomainEnabled: typeof fetchedSettings.isCustomDomainEnabled === 'string'
          ? fetchedSettings.isCustomDomainEnabled === 'true'
          : Boolean(fetchedSettings.isCustomDomainEnabled),
        logo: fetchedSettings.logo || null,
        logoPreview: fetchedSettings.logo || null,
        business_name: fetchedSettings.business_name || "",
        featuredArticles: [],
      })
    } else {
      setSettings(emptySettings())
    }
    setInitialSyncDone(true)
  }, [fetchedSettings, hasFetched, lastFetchedGroupId, selectedGroupId])

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (event) => {
        setSettings((prev) => ({
          ...prev,
          logo: file,
          logoPreview: event.target.result,
        }))
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSaveChanges = () => {
    const formData = new FormData();
    formData.append('business_id', business_id);
    formData.append('group_id', selectedGroupId);
    formData.append('brand_color', settings.brand_color);
    formData.append('accent_color', settings.accent_color);
    formData.append('font_family', settings.font_family);
    // formData.append('custom_domain', settings.custom_domain);
    // formData.append('base_path', settings.base_path || '/');
    formData.append('isCustomDomainEnabled', settings.isCustomDomainEnabled ? true : false);
    formData.append('business_name', settings.business_name);

    if (settings.logo instanceof File) {
      formData.append('logo', settings.logo);
    } else if (settings.logo) {
      formData.append('logo', settings.logo);
    }

    dispatch(saveKbSettings(formData))
      .unwrap()
      .then(() => {
        toast.success("Settings saved successfully")
      })
      .catch((error) => {
        toast.error(error || "Failed to save settings")
      });
  }

  return (
    <Box sx={{ minHeight: "100vh", pt: '16px', fontFamily: "Inter" }}>
      <Container maxWidth={false} sx={{ px: 3 }}>
        {/* Legacy in-page title + group — hidden when header shows them (Application V2) */}
        {!isHeaderInlinePageTitleEnabled && (
          <div className="profile-header-row" style={{ marginBottom: '27px' }}>
            <Typography variant="h5" sx={{ fontWeight: 500, color: "#061522", fontFamily: "Inter", fontSize: '24px' }}>
              Customization
            </Typography>
            <GroupSelectDropdown
              onSelect={setSelectedGroupId}
              value={selectedGroupId}
            />
          </div>
        )}

        <Box sx={{ display: "flex", gap: "24px" }}>
          <SettingsSidebar
            settings={settings}
            onSettingChange={handleSettingChange}
            onLogoUpload={handleLogoUpload}
            onSaveChanges={handleSaveChanges}
            loading={settingsLoading}
            initialSyncDone={initialSyncDone}
            groupId={selectedGroupId}
          />

          <PreviewSection settings={settings} currentTab={currentTab} onTabChange={setCurrentTab} loading={isInitialLoading} groupId={selectedGroupId} />
        </Box>
      </Container>
    </Box>
  )
}
